import { Box, Grid, Link, Stack, Text } from '@chakra-ui/react';
import { FiDownload } from 'react-icons/fi';
import PageContainer from '../components/PageContainer';
import { focuses, leaderShipSkills, techSkills } from '../constants/more';
import { appColors } from '../constants/theme';
import { t } from '../locales/locales';

const DownloadIcon = FiDownload as React.ComponentType<{
  size?: number;
  color?: string;
}>;

export default function More() {
  return (
    <PageContainer
      props={{
        mb: 4,
      }}
    >
      <Header />
      <Separator />
      <AboutMe />
      <Separator />
      <Focus />
      <Separator />
      <SkillsAndExpertise />
    </PageContainer>
  );
}

const Header = () => {
  return (
    <Box
      alignItems="center"
      display="flex"
      flexDirection="column"
      alignSelf={'center'}
    >
      <Box
        maxWidth={{ base: 320, md: 400, lg: 500 }}
        alignItems="center"
        display="flex"
        flexDirection="column"
        alignSelf={'center'}
      >
        <Text
          fontSize={'3xl'}
          color="white"
          textAlign={'center'}
          fontWeight={'medium'}
          mb={2}
          mt={8}
        >
          {t('name')}
        </Text>
        <Text
          fontSize={{ base: 'sm', md: 'sm' }}
          color="gray.300"
          textAlign={'center'}
          fontWeight={'regular'}
        >
          {t('title')}
        </Text>
        <Text
          fontSize={{ base: 'sm', md: 'sm' }}
          color="gray.300"
          textAlign={'center'}
          fontWeight={'regular'}
        >
          {t('specialties')}
        </Text>
        <Text
          fontSize={{ base: 'sm', md: 'sm' }}
          color="gray.300"
          textAlign={'center'}
          fontWeight={'regular'}
          mt={5}
        >
          {t('subtitle')}
        </Text>
        <Link
          href="/Mustafa_Kamel_2025.pdf"
          download
          mt={{ base: 3, md: 5 }}
          bg={appColors.hover}
          color="white"
          px={{ base: 2, md: 6 }}
          py={{ base: 1, md: 1 }}
          borderRadius="md"
          display="inline-flex"
          alignItems="center"
          gap={2}
          _hover={{
            bg: appColors.primary,
            transform: 'translateY(-2px)',
            textDecoration: 'none',
          }}
        >
          <DownloadIcon size={18} color="white" />
          {t('download')}
        </Link>
      </Box>
    </Box>
  );
};

const AboutMe = () => {
  return (
    <Box marginX={4} textAlign={'center'}>
      <Text color="gray.300" fontWeight={'medium'}>
        {t('preview')}
      </Text>
    </Box>
  );
};

const SkillsAndExpertise = () => {
  return (
    <Box maxW="1000px" mx="auto">
      <Stack
        direction={{ base: 'column', md: 'row' }}
        align={{ base: 'center', md: 'flex-start' }}
        justify="center"
        position="relative"
      >
        <Box textAlign={{ base: 'center', md: 'left' }}>
          <Text
            color="white"
            fontWeight="semibold"
            mb={2}
            textAlign={{ base: 'center', md: 'left' }}
          >
            {t('techSkills')}
          </Text>
          <Box mt={2} display="flex" flexDirection="column" gap={3}>
            {techSkills.map((skill) => (
              <Box
                key={skill}
                display="flex"
                alignItems="center"
                gap={2}
                justifyContent={{ base: 'center', md: 'flex-start' }}
              >
                <Box
                  width="6px"
                  height="6px"
                  borderRadius="full"
                  bg="blue.400"
                  boxShadow="0 0 8px rgba(66,153,225,0.8)"
                />
                <Text color="gray.400" fontSize={{ base: 'sm', md: 'sm' }}>
                  {skill}
                </Text>
              </Box>
            ))}
          </Box>
        </Box>
        <Box
          display={{ base: 'none', md: 'block' }}
          width="1px"
          bg="rgba(255,255,255,0.08)"
          alignSelf="stretch"
          position="relative"
        >
          <Box
            position="absolute"
            top="20%"
            bottom="20%"
            left="0"
            right="0"
            bg="linear-gradient(to bottom, transparent, rgba(66,153,225,0.5), transparent)"
            filter="blur(2px)"
          />
        </Box>
        <Box textAlign={{ base: 'center', md: 'left' }} marginStart={2}>
          <Text
            color="white"
            fontWeight="semibold"
            mb={2}
            textAlign={{ base: 'center', md: 'left' }}
          >
            {t('leadershipSkills')}
          </Text>
          <Box mt={2} display="flex" flexDirection="column" gap={3}>
            {leaderShipSkills?.map((skill) => (
              <Box
                key={skill}
                display="flex"
                alignItems="center"
                gap={2}
                justifyContent={{ base: 'center', md: 'flex-start' }}
              >
                <Box
                  width="6px"
                  height="6px"
                  borderRadius="full"
                  bg="blue.400"
                  boxShadow="0 0 8px rgba(66,153,225,0.8)"
                />
                <Text color="gray.400" fontSize={{ base: 'sm', md: 'sm' }}>
                  {skill}
                </Text>
              </Box>
            ))}
          </Box>
        </Box>
      </Stack>
    </Box>
  );
};

const Focus = () => {
  return (
    <Box px={4}>
      <Text color="white" textAlign={'center'}>
        {t('focus_on')}
      </Text>
      <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }}>
        {focuses.map((focus) => (
          <Box py={{ base: 2, md: 3 }} px={{ base: 2, md: 6 }}>
            <Box
              position="relative"
              border="1px solid"
              borderColor="rgba(255,255,255,0.08)"
              borderRadius="2xl"
              p={{ base: 2, md: 2 }}
              overflow="hidden"
              _hover={{
                borderColor: 'rgba(66,153,225,0.6)',
                boxShadow: '0 0 40px rgba(66,153,225,0.15)',
                transform: 'translateY(-4px)',
              }}
              transition="all 0.3s ease"
            >
              <Box
                position="absolute"
                top="0"
                left="0"
                right="0"
                height="2px"
                bgGradient="linear(to-r, transparent, blue.400, transparent)"
                opacity={0.8}
              />
              <Box
                position="absolute"
                top="-40px"
                right="-40px"
                width="160px"
                height="160px"
                bg="radial-gradient(circle, rgba(66,153,225,0.25) 0%, rgba(66,153,225,0.08) 40%, transparent 70%)"
                filter="blur(40px)"
                pointerEvents="none"
              />
              <Box
                position="absolute"
                bottom="-50px"
                left="-50px"
                width="180px"
                height="180px"
                bg="radial-gradient(circle, rgba(66,153,225,0.18) 0%, rgba(66,153,225,0.06) 40%, transparent 70%)"
                filter="blur(50px)"
                pointerEvents="none"
              />
              <Box
                display="flex"
                gap={{ base: 2, md: 6 }}
                alignItems="flex-start"
              >
                <Box mt={{ base: 1, md: 1 }}>
                  {<focus.icon size={24} color="white" />}
                </Box>
                <Box flex="1">
                  <Text
                    fontSize={{ base: 'small', md: 'md' }}
                    fontWeight="bold"
                    color="white"
                  >
                    {focus?.title}
                  </Text>
                  <Text
                    mt={1}
                    fontSize={{ base: 'sm', md: 'md' }}
                    color="gray.300"
                    lineHeight={{ base: '1.3', md: '1.4' }}
                    maxW="800px"
                  >
                    {focus?.desc}
                  </Text>
                </Box>
              </Box>
            </Box>
          </Box>
        ))}
      </Grid>
    </Box>
  );
};

const Separator = () => {
  return (
    <Box
      background={'gray.900'}
      width={'fill'}
      height={'0.5'}
      marginX={2}
      marginY={{ base: 5, md: 10 }}
    />
  );
};
